import React, { useState, useCallback } from 'react';
import { Compass, CheckCircle, ArrowRight, Download } from 'lucide-react';

interface AssessmentItem {
  subdomain: string;
  tier: string;
  item_id: string;
  text: string;
  reverse_coded: boolean;
}

interface LocusScores {
  [subdomain: string]: {
    [tier: string]: number;
  };
}

// Assessment items (42 items from clarity_items_v1.csv)
const assessmentItems: AssessmentItem[] = [
  { subdomain: 'context', tier: 'individual', item_id: 'CX-I-01', text: 'I can accurately describe the conditions and constraints shaping my work.', reverse_coded: false },
  { subdomain: 'context', tier: 'individual', item_id: 'CX-I-02', text: 'Important environmental factors often catch me by surprise.', reverse_coded: true },
  { subdomain: 'context', tier: 'interpersonal', item_id: 'CX-IP-01', text: 'Our team describes the current situation in similar terms.', reverse_coded: false },
  { subdomain: 'context', tier: 'interpersonal', item_id: 'CX-IP-02', text: 'Teammates disagree on basic facts about our context.', reverse_coded: true },
  { subdomain: 'context', tier: 'collective', item_id: 'CX-C-01', text: 'Leadership narratives match front-line evidence.', reverse_coded: false },
  { subdomain: 'context', tier: 'collective', item_id: 'CX-C-02', text: 'Official updates conflict with what people actually see.', reverse_coded: true },
  { subdomain: 'intent', tier: 'individual', item_id: 'IN-I-01', text: 'I know why my current work matters.', reverse_coded: false },
  { subdomain: 'intent', tier: 'individual', item_id: 'IN-I-02', text: 'My daily tasks feel disconnected from any meaningful purpose.', reverse_coded: true },
  { subdomain: 'intent', tier: 'interpersonal', item_id: 'IN-IP-01', text: "We can explain each other's intentions without guessing.", reverse_coded: false },
  { subdomain: 'intent', tier: 'interpersonal', item_id: 'IN-IP-02', text: 'Hidden agendas or unspoken motives shape our coordination.', reverse_coded: true },
  { subdomain: 'intent', tier: 'collective', item_id: 'IN-C-01', text: "The organization's purpose is clear and consistently referenced.", reverse_coded: false },
  { subdomain: 'intent', tier: 'collective', item_id: 'IN-C-02', text: 'Different leaders promote conflicting purpose statements.', reverse_coded: true },
  { subdomain: 'meaning', tier: 'individual', item_id: 'ME-I-01', text: 'I use key terms and metrics the same way they are defined.', reverse_coded: false },
  { subdomain: 'meaning', tier: 'individual', item_id: 'ME-I-02', text: 'I often reinterpret shared terms to fit my understanding.', reverse_coded: true },
  { subdomain: 'meaning', tier: 'interpersonal', item_id: 'ME-IP-01', text: 'We verify meanings of important terms before deciding.', reverse_coded: false },
  { subdomain: 'meaning', tier: 'interpersonal', item_id: 'ME-IP-02', text: 'We argue because we use the same words to mean different things.', reverse_coded: true },
  { subdomain: 'meaning', tier: 'collective', item_id: 'ME-C-01', text: 'The organization maintains a clear glossary or schema for core concepts.', reverse_coded: false },
  { subdomain: 'meaning', tier: 'collective', item_id: 'ME-C-02', text: 'Success criteria vary by team without reconciliation.', reverse_coded: true },
  { subdomain: 'role', tier: 'individual', item_id: 'RO-I-01', text: 'I am clear on what I own and what I don\'t.', reverse_coded: false },
  { subdomain: 'role', tier: 'individual', item_id: 'RO-I-02', text: 'I frequently do work that should belong to someone else.', reverse_coded: true },
  { subdomain: 'role', tier: 'interpersonal', item_id: 'RO-IP-01', text: 'Interfaces and handoffs between collaborators are explicit.', reverse_coded: false },
  { subdomain: 'role', tier: 'interpersonal', item_id: 'RO-IP-02', text: 'Tasks fall through the cracks because ownership is unclear.', reverse_coded: true },
  { subdomain: 'role', tier: 'collective', item_id: 'RO-C-01', text: 'Roles and accountabilities are documented and current.', reverse_coded: false },
  { subdomain: 'role', tier: 'collective', item_id: 'RO-C-02', text: 'Reorgs or exceptions routinely bypass defined ownership.', reverse_coded: true },
  { subdomain: 'priority', tier: 'individual', item_id: 'PR-I-01', text: 'I can rank my current work by impact and urgency.', reverse_coded: false },
  { subdomain: 'priority', tier: 'individual', item_id: 'PR-I-02', text: 'Everything feels equally important.', reverse_coded: true },
  { subdomain: 'priority', tier: 'interpersonal', item_id: 'PR-IP-01', text: 'We resolve priority conflicts quickly with shared criteria.', reverse_coded: false },
  { subdomain: 'priority', tier: 'interpersonal', item_id: 'PR-IP-02', text: 'Teams compete for attention without a clear tie-breaker.', reverse_coded: true },
  { subdomain: 'priority', tier: 'collective', item_id: 'PR-C-01', text: 'There is a visible hierarchy of priorities that guides choices.', reverse_coded: false },
  { subdomain: 'priority', tier: 'collective', item_id: 'PR-C-02', text: 'Projects win resources through politics more than principles.', reverse_coded: true },
  { subdomain: 'signal', tier: 'individual', item_id: 'SI-I-01', text: 'I know which data and feedback to trust.', reverse_coded: false },
  { subdomain: 'signal', tier: 'individual', item_id: 'SI-I-02', text: 'Noisy inputs regularly distract my attention.', reverse_coded: true },
  { subdomain: 'signal', tier: 'interpersonal', item_id: 'SI-IP-01', text: 'We interpret key metrics the same way.', reverse_coded: false },
  { subdomain: 'signal', tier: 'interpersonal', item_id: 'SI-IP-02', text: 'Different teams react differently to the same signal.', reverse_coded: true },
  { subdomain: 'signal', tier: 'collective', item_id: 'SI-C-01', text: 'Reporting systems are timely, transparent, and credible.', reverse_coded: false },
  { subdomain: 'signal', tier: 'collective', item_id: 'SI-C-02', text: 'Leaders cite data that others consider unreliable.', reverse_coded: true },
  { subdomain: 'action', tier: 'individual', item_id: 'AC-I-01', text: 'My next step is specific and time-bound.', reverse_coded: false },
  { subdomain: 'action', tier: 'individual', item_id: 'AC-I-02', text: 'I hesitate because I\'m unsure what to do next.', reverse_coded: true },
  { subdomain: 'action', tier: 'interpersonal', item_id: 'AC-IP-01', text: 'Handoffs happen on time with clear acceptance criteria.', reverse_coded: false },
  { subdomain: 'action', tier: 'interpersonal', item_id: 'AC-IP-02', text: 'Work bounces back and forth due to unclear expectations.', reverse_coded: true },
  { subdomain: 'action', tier: 'collective', item_id: 'AC-C-01', text: 'Cross-team actions align with the same plan and cadence.', reverse_coded: false },
  { subdomain: 'action', tier: 'collective', item_id: 'AC-C-02', text: 'Parallel teams move out of sync and create avoidable thrash.', reverse_coded: true },
];

const subdomains = ['context', 'intent', 'meaning', 'role', 'priority', 'signal', 'action'];
const tiers = ['individual', 'interpersonal', 'collective'];

export const ClarityCompass: React.FC = () => {
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [results, setResults] = useState<any>(null);
  const [isComputing, setIsComputing] = useState(false);

  const handleResponse = useCallback((itemId: string, value: number) => {
    setResponses(prev => ({ ...prev, [itemId]: value }));
  }, []);

  const computeLocusScores = useCallback((): LocusScores => {
    const scores: LocusScores = {};
    
    // Group items by subdomain and tier
    subdomains.forEach(subdomain => {
      scores[subdomain] = {};
      tiers.forEach(tier => {
        const items = assessmentItems.filter(
          item => item.subdomain === subdomain && item.tier === tier
        );
        
        const itemScores = items.map(item => {
          const rawScore = responses[item.item_id] || 3;
          // Reverse code if needed (6 - score)
          return item.reverse_coded ? 6 - rawScore : rawScore;
        });
        
        // Mean of the two items for this locus
        const mean = itemScores.reduce((a, b) => a + b, 0) / itemScores.length || 3;
        scores[subdomain][tier] = Math.round(mean * 10) / 10;
      });
    });
    
    return scores;
  }, [responses]);

  const computeResults = useCallback(async () => {
    setIsComputing(true);
    
    // Check if all items are answered
    const allAnswered = assessmentItems.every(item => responses[item.item_id] !== undefined);
    if (!allAnswered) {
      alert('Please answer all questions before computing results.');
      setIsComputing(false);
      return;
    }

    const locusScores = computeLocusScores();
    
    try {
      // Import and use the client-side computation function
      const { compute } = await import('../utils/clarityCompass');
      const output = compute(locusScores);
      setResults(output);
    } catch (error) {
      console.error('Error computing results:', error);
      alert('Error computing results. Please try again.');
    } finally {
      setIsComputing(false);
    }
  }, [responses, computeLocusScores]);

  const progress = (Object.keys(responses).length / assessmentItems.length) * 100;
  const subdomainProgress = subdomains.map(subdomain => {
    const items = assessmentItems.filter(item => item.subdomain === subdomain);
    const answered = items.filter(item => responses[item.item_id] !== undefined).length;
    return { subdomain, progress: (answered / items.length) * 100 };
  });

  if (results) {
    return (
      <div className="min-h-screen bg-[var(--surface)]">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Compass className="h-8 w-8 text-consulting-slate-blue-600" />
              <h1 className="text-3xl font-heading font-bold text-charcoal-900">Clarity Compass Results</h1>
            </div>

            <div className="space-y-6">
              <div className="bg-consulting-slate-blue-50 p-6 border border-consulting-slate-blue-200">
                <h2 className="text-xl font-heading font-semibold text-charcoal-900 mb-2">
                  {results.narrative.summary_label} Clarity
                  {results.narrative.stability_modifier && ` — ${results.narrative.stability_modifier}`}
                </h2>
                <p className="text-charcoal-700">
                  Overall Clarity Index (OCI): <strong className="text-charcoal-900">{results.computed.OCI}</strong>
                </p>
                {results.narrative.primary_archetype && (
                  <div className="mt-4 p-4 bg-[var(--bg-raised)] border border-consulting-slate-blue-300">
                    <p className="font-semibold text-charcoal-900">{results.narrative.primary_archetype.name}</p>
                    <p className="text-sm text-charcoal-600">{results.narrative.primary_archetype.headline}</p>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[var(--surface)] p-4">
                  <h3 className="font-semibold text-charcoal-900 mb-2">Subdomain Scores (SCS)</h3>
                  <div className="space-y-2 text-sm">
                    {Object.entries(results.computed.SCS).map(([subdomain, score]) => (
                      <div key={subdomain} className="flex justify-between">
                        <span className="capitalize text-charcoal-700">{subdomain}</span>
                        <span className="font-medium text-charcoal-900">{(score as number).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[var(--surface)] p-4">
                  <h3 className="font-semibold text-charcoal-900 mb-2">Tier Scores (TCS)</h3>
                  <div className="space-y-2 text-sm">
                    {Object.entries(results.computed.TCS).map(([tier, score]) => (
                      <div key={tier} className="flex justify-between">
                        <span className="capitalize text-charcoal-700">{tier}</span>
                        <span className="font-medium text-charcoal-900">{(score as number).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {results.narrative.pattern && (
                <div className="bg-[var(--slate-dim)] p-6 border border-[var(--slate)]">
                  <h3 className="font-semibold text-charcoal-900 mb-2">Cross-Tier Pattern</h3>
                  <p className="text-sm font-medium text-charcoal-800 mb-1">{results.narrative.pattern.name} ({results.narrative.pattern.id})</p>
                  <p className="text-sm text-charcoal-600">{results.narrative.pattern.narrative}</p>
                </div>
              )}

              {results.narrative.interventions && results.narrative.interventions.length > 0 && (
                <div className="bg-[var(--mustard-dim)] p-6 border border-[var(--mustard)]">
                  <h3 className="font-semibold text-charcoal-900 mb-3">Recommended Interventions</h3>
                  <ul className="space-y-2">
                    {results.narrative.interventions.map((intervention: any, idx: number) => (
                      <li key={idx} className="text-sm text-charcoal-700">
                        <span className="font-medium capitalize">{intervention.subdomain}</span> → <strong>{intervention.discipline}</strong>: {intervention.prescription}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-[var(--surface)] p-6 border border-charcoal-200">
                <h3 className="font-semibold text-charcoal-900 mb-3">Tier Guidance</h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(results.narrative.tier_guidance).map(([tier, guidance]) => (
                    <div key={tier} className="flex items-start gap-2">
                      <span className="font-medium capitalize text-charcoal-700 min-w-[120px]">{tier}:</span>
                      <span className="text-charcoal-600">{guidance as string}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-consulting-slate-blue-50 p-4 border border-consulting-slate-blue-200">
                <p className="text-sm text-charcoal-700">{results.narrative.closing}</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setResults(null);
                    setResponses({});
                  }}
                  className="px-4 py-2 bg-charcoal-200 text-charcoal-900 hover:bg-charcoal-300 transition-colors"
                >
                  Start Over
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `clarity-compass-${Date.now()}.json`;
                    a.click();
                  }}
                  className="px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Compass className="h-8 w-8 text-consulting-slate-blue-600" />
            <div>
              <h1 className="text-3xl font-heading font-bold text-charcoal-900">Clarity Compass</h1>
              <p className="text-charcoal-600 mt-1">
                Diagnose shared reality integrity across seven subdomains and three tiers
              </p>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-charcoal-700">Overall Progress</span>
              <span className="text-sm text-charcoal-600">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-charcoal-200 rounded-full h-2">
              <div 
                className="bg-consulting-slate-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {subdomainProgress.map(({ subdomain, progress }) => (
              <div key={subdomain} className="text-center">
                <div className="text-xs font-medium text-charcoal-600 mb-1 capitalize">{subdomain}</div>
                <div className="w-full bg-charcoal-200 rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full transition-all ${
                      progress === 100 ? 'bg-[var(--green)]' : progress > 0 ? 'bg-consulting-slate-blue-600' : 'bg-charcoal-200'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {subdomains.map(subdomain => {
          const items = assessmentItems.filter(item => item.subdomain === subdomain);
          const isExpanded = currentSection === subdomain;
          
          return (
            <div key={subdomain} className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 mb-4 overflow-hidden">
              <button
                onClick={() => setCurrentSection(isExpanded ? null : subdomain)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--surface)] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg font-heading font-semibold text-charcoal-900 capitalize">{subdomain}</span>
                  <span className="text-xs text-charcoal-500 bg-[var(--hover-tint)] px-2 py-1 rounded">
                    {items.filter(item => responses[item.item_id] !== undefined).length}/{items.length}
                  </span>
                </div>
                <ArrowRight 
                  className={`h-5 w-5 text-charcoal-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
              </button>

              {isExpanded && (
                <div className="px-6 pb-6 space-y-6 border-t border-charcoal-200">
                  {tiers.map(tier => {
                    const tierItems = items.filter(item => item.tier === tier);
                    return (
                      <div key={tier} className="pt-4">
                        <h3 className="text-sm font-semibold text-charcoal-700 mb-3 capitalize">{tier}</h3>
                        <div className="space-y-4">
                          {tierItems.map(item => (
                            <div key={item.item_id} className="bg-[var(--surface)] p-4 border border-charcoal-200">
                              <p className="text-sm text-charcoal-700 mb-4">{item.text}</p>
                              <div className="space-y-2">
                                {[
                                  { value: 1, label: 'Fragmented', description: 'No shared understanding or systemic confusion' },
                                  { value: 2, label: 'Shared Language', description: 'Superficial agreement without coherence' },
                                  { value: 3, label: 'Operational', description: 'Functional clarity for local coordination' },
                                  { value: 4, label: 'Systemic', description: 'Formalized mechanisms maintain shared understanding' },
                                  { value: 5, label: 'Reflexive', description: 'Clarity is embedded culturally and self-corrects' }
                                ].map(({ value, label, description }) => {
                                  const isSelected = responses[item.item_id] === value;
                                  return (
                                    <button
                                      key={value}
                                      onClick={() => handleResponse(item.item_id, value)}
                                      className={`w-full py-3 px-4  text-sm font-medium transition-all text-left ${
                                        isSelected
                                          ? 'bg-consulting-slate-blue-600 text-white shadow-md'
                                          : 'bg-[var(--bg-raised)] text-charcoal-700 border border-charcoal-300 hover:border-consulting-slate-blue-400 hover:bg-consulting-slate-blue-50'
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                          isSelected ? 'bg-[var(--bg-raised)] text-consulting-slate-blue-600' : 'bg-charcoal-200 text-charcoal-700'
                                        }`}>
                                          {value}
                                        </span>
                                        <div className="flex-1">
                                          <div className="font-semibold">{label}</div>
                                          <div className={`text-xs mt-0.5 ${isSelected ? 'text-consulting-slate-blue-100' : 'text-charcoal-500'}`}>
                                            {description}
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div className="mt-6 flex justify-end">
          <button
            onClick={computeResults}
            disabled={isComputing || Object.keys(responses).length < assessmentItems.length}
            className="px-6 py-3 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors disabled:bg-charcoal-300 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
          >
            {isComputing ? (
              <>Computing...</>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                Compute Results
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};